import React, { useMemo, useState } from "react";
import { Option } from "react-select/lib/filters";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Loading, Box, Divider, Heading, Button } from "@foris/avocado-suite";
import { classRoomSearchMutation } from "../graphql/advanceSearch.queries";
import AdvanceCampusSelector from "@shared/ui/advance-search/advance-campus-selector";
import AdvancedBuildingSelector from "@shared/ui/advance-search/advanced-building-selector";
import { ClassRoom } from "@modules/shared/selectors";
import { Capacity, TimeRange } from "@modules/shared/inputs";
import { Days } from "@modules/shared/checks";
import EmptyResultsModal from "@modules/shared/empty-results-modal";
import WeeksRange from "@modules/shared/weeksRange/WeeksRange";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { objectEmpty } from "@utils/validations";
import { validations } from "./utils";
import { validations as advanceSearchValidations } from "@services/collections/advance-search";
import { getAdvancedSelectorsFilters } from "@dimensions/utils/searchDimension";
import {
  ITimeRangeDependency,
  IWeeksRangeDependency,
} from "@services/types/advance-search/advanceSearchTypes";
import {
  setAdvancedSearchClassroomType,
  setAdvancedSearchProperty,
  setAdvancedSearchCapacity,
  useAdvancedSearchStore,
  setAdvancedSearchDays,
  setAdvancedSearchError,
  setAdvancedSearchTimeRangeError,
  setAdvancedSearchDaysError,
  setAdvancedSearchBlockRange,
  setAdvancedSearchIntervals,
  setAdvancedSearchIntervalRange,
} from "@context/useAdvancedSearchStore";
import css from "./advanceSearch.module.scss";

const AdvanceSearch: React.FC<any> = (props: any) => {
  // States for validations
  const error = useAdvancedSearchStore(state => state?.error);
  const daysError = useAdvancedSearchStore(state => state?.daysError);
  const timeRangeError = useAdvancedSearchStore(state => state?.timeRangeError);
  const [isEmpty, setIsEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  // Hooks
  const { t } = useTranslation();
  const history = useHistory();
  const { origin, scenario } = useContextUrl();
  const { advancedSearchResultsUrl } = useNavigationUrl();
  const [advanceSearchMutation] = useMutation(classRoomSearchMutation, {});

  // Selector properties
  const selectedCampusOption = useAdvancedSearchStore(state => state?.properties?.campus);
  const selectedCampus = selectedCampusOption?.value ?? null;
  const selectedBuildingOption = useAdvancedSearchStore(state => state?.properties?.building);
  const selectedBuilding = selectedBuildingOption?.value ?? null;
  const selectedClassroomTypeOption = useAdvancedSearchStore(state => state?.classroomType);
  const selectedClassroomType = selectedClassroomTypeOption?.value ?? null;
  const objCapacity = useAdvancedSearchStore(state => state?.objCapacity) ?? ({} as any);
  const days = useAdvancedSearchStore(state => state?.days) ?? [];
  const blockRange = useAdvancedSearchStore(state => state?.blockRange) ?? {};
  const intervalRange = useAdvancedSearchStore(state => state?.intervalRange) ?? null;
  const intervals = useAdvancedSearchStore(state => state?.intervals) ?? null;

  const objAvailability = useMemo(() => {
    const newObj: any = {
      availability: {
        dayId: days,
        blockRange,
        intervalRange,
        intervals,
      },
    };

    if (!days?.length) {
      delete newObj.availability.dayId;
    }

    if (!blockRange?.start && !blockRange?.end) {
      delete newObj.availability.blockRange;
    }

    if (!intervalRange) {
      delete newObj.availability.intervalRange;
    }

    if (!intervals?.length) {
      delete newObj.availability.intervals;
    }

    return Object.keys(newObj?.availability)?.length > 0 ? newObj : {};
  }, [days, blockRange, intervalRange, intervals]);

  const hasSomeFilter = useMemo(() => {
    return (
      !objectEmpty(objCapacity) ||
      !objectEmpty(objAvailability) ||
      selectedBuilding ||
      selectedCampus ||
      selectedClassroomType
    );
  }, [objCapacity, objAvailability, selectedBuilding, selectedCampus, selectedClassroomType]);

  const hasSomeError = error || daysError || timeRangeError;

  const handleSelectCampus = (option: Option) => {
    setAdvancedSearchProperty("campus", option);
    setAdvancedSearchProperty("building", null);
  };

  const setCapacity = (values: any, error: any) => {
    if (error) {
      setAdvancedSearchError(true);
      return;
    }

    const validation = validations.capacity(values, objCapacity);
    setAdvancedSearchCapacity(validation.objCapacity);
    setAdvancedSearchError(validation.error);
  };

  const setDays = (value: number[]) => {
    const validation = advanceSearchValidations.days(value, objAvailability);
    setAdvancedSearchDays(validation?.availability?.availability?.dayId ?? []);
    setAdvancedSearchError(validation.error);
    setAdvancedSearchTimeRangeError(validation.timeRangeError);
    setAdvancedSearchDaysError(validation.daysError);
  };
  const setTimeRange = (value: ITimeRangeDependency, error: boolean) => {
    if (error) {
      setAdvancedSearchError(true);
      return;
    }
    const validation = advanceSearchValidations.timeRange(value, objAvailability);
    setAdvancedSearchBlockRange(validation?.availability?.availability?.blockRange);
    setAdvancedSearchError(validation.error);
    setAdvancedSearchTimeRangeError(validation.timeRangeError);
    setAdvancedSearchDaysError(validation.daysError);
  };

  const setWeeksRange = (values: IWeeksRangeDependency) => {
    const validation = advanceSearchValidations.weeksRange(values, objAvailability);

    setAdvancedSearchIntervalRange(validation?.availability?.availability?.intervalRange ?? null);
    setAdvancedSearchIntervals(validation?.availability?.availability?.intervals ?? null);
  };

  let weeksDisabled = true;
  if (objAvailability.availability) {
    const daysExist = objAvailability.availability.hasOwnProperty("dayId");
    const rangeExist = objAvailability.availability.hasOwnProperty("blockRange");
    if (rangeExist) {
      const start = objAvailability.availability.blockRange.hasOwnProperty("start");
      const end = objAvailability.availability.blockRange.hasOwnProperty("end");
      if (start && end && daysExist) weeksDisabled = false;
    }
  }

  const createObjectMutation = () => {
    const filterArray = [];

    const selectorsFilterArray = getAdvancedSelectorsFilters({
      campusId: selectedCampus,
      buildingId: selectedBuilding,
    });

    !objectEmpty(objCapacity) && filterArray.push(objCapacity);
    !objectEmpty(objAvailability) && filterArray.push(objAvailability);
    !!selectedClassroomType &&
      filterArray.push({
        classroomTypeId: {
          eq: selectedClassroomType,
        },
      });

    const mutationFilter = {
      originId: parseInt(origin),
      scenarioId: parseInt(scenario),
      filter: {
        fields: {
          and: [
            // Set default filter 'cause now we bring only the ones with assignment
            { hasEvents: true },
            ...selectorsFilterArray,
            ...filterArray,
          ],
        },
      },
      pagination: {
        page: 1,
        size: 10,
      },
    };
    return mutationFilter;
  };

  const callUpdateHook = async () => {
    setLoading(true);
    try {
      const variables = createObjectMutation();
      const { data } = await advanceSearchMutation({ variables });
      setLoading(false);
      const dataItems = data.filter.createClassroomFilter;
      if (dataItems.classrooms.items.length > 0) {
        const idSearch = dataItems.id;
        history.push(advancedSearchResultsUrl("classroom", idSearch));
      } else {
        setIsEmpty(true);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <div className={css.ctnAdvanceSearch}>
      <EmptyResultsModal show={isEmpty} onClose={() => setIsEmpty(false)} />

      <Box className={css.ctnAdvanceSearch_boxContent}>
        <div className={css.selectorContainer}>
          <AdvanceCampusSelector
            campusOption={selectedCampusOption}
            scenario={scenario}
            onChange={handleSelectCampus}
          />
          <AdvancedBuildingSelector
            key={`building-${selectedCampus}`}
            scenario={scenario}
            campusId={selectedCampus}
            buildingOption={selectedBuildingOption}
            onChange={option =>
              setAdvancedSearchProperty("building", option?.value ? option : null)
            }
          />
        </div>
        <Divider className={css.divider} />
        <div className={css.selectorContainer}>
          <ClassRoom
            label
            labelValue={t("advanced-search.classroom.forms.select-type-classroom.label")}
            placeholder={t("advanced-search.classroom.forms.select-type-classroom.placeholder")}
            className={css.typeClassroom}
            value={selectedClassroomTypeOption}
            onCallback={option => setAdvancedSearchClassroomType(option?.value ? option : null)}
          />
        </div>
        <Divider className={css.divider} />
        <Capacity
          onCallback={(values, error) => setCapacity(values, error)}
          minInitialValue={(objCapacity as any)?.capacity?.value?.gte ?? ""}
          maxInitialValue={(objCapacity as any)?.capacity?.value?.lte ?? ""}
        />
        <Divider className={css.divider} />
        <Heading type="h4">{t("advanced-search.classroom.forms.days.title")}</Heading>
        <Days
          required={daysError}
          initialValue={objAvailability?.availability?.dayId ?? []}
          onCallback={values => setDays(values)}
        />
        <Divider className={css.divider} />
        <Heading type="h4">{t("advanced-search.classroom.forms.time-range.title")}</Heading>
        <TimeRange
          minInitialValue={blockRange?.start ?? ""}
          maxInitialValue={blockRange?.end ?? ""}
          required={timeRangeError}
          onCallback={(values, error) => setTimeRange(values, error)}
        />
        <Divider className={css.divider} />
        <WeeksRange
          {...props}
          initialExternalWeeks={
            intervalRange
              ? {
                  min: intervalRange?.start ?? 1,
                  max: intervalRange?.end ?? 1,
                }
              : undefined
          }
          disabled={weeksDisabled}
          onCallback={values => setWeeksRange(values)}
        />
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <Button
          className={css.ctnAdvanceSearch_actionButton}
          disabled={hasSomeError || !hasSomeFilter}
          onClick={() => callUpdateHook()}
        >
          {t("advanced-search.classroom.forms.btn-search")}
        </Button>
      )}
    </div>
  );
};

export default AdvanceSearch;
